import type { FC } from 'react';
import React, { useContext, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import idx from 'idx';
import { defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import type { ValueType } from '@atlaskit/select';
import Select from '@atlaskit/select';

import { FiltersContext } from './FiltersContext';
import { LabelsQuery } from './LabelsQuery.graphql';
import type {
	SpaceDirectoryLabelsQuery as LabelsQueryType,
	SpaceDirectoryLabelsQueryVariables as LabelsQueryVariables,
} from './__types__/LabelsQuery';

const LABELS_FILTER_DESCRIPTION_ID = 'labels-filter-description';

type Option = {
	label: string;
	value?: string;
};

const i18n = defineMessages({
	allCategories: {
		id: 'space-directory.filters.labels.all-categories',
		defaultMessage: 'All categories',
		description: 'Name of a filter that will show all spaces regardless of their category',
	},
	categoryFilterLabel: {
		id: 'space-directory.filters.labels.aria-labelledby.text',
		defaultMessage: 'Filter by category',
		description: 'Label of the Space directory category filter combobox',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ScreenReaderOnlyText = styled.span({
	border: 0,
	clip: 'rect(1px, 1px, 1px, 1px)',
	'clip-path': 'inset(50%)',
	height: '1px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	margin: '-1px',
	overflow: 'hidden',
	padding: '0',
	position: 'absolute',
	width: '1px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	'word-wrap': 'normal !important',
});

const selectStyles = {
	container: (base) => ({
		...base,
		width: '142px',
		minHeight: '32px',
		marginLeft: '9px',
	}),
	control: (base) => ({
		...base,
		minHeight: '32px',
		height: '32px',
		flexWrap: 'nowrap',
	}),
};

export const LabelsFilter: FC = () => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const { data, loading, fetchMore } = useQuery<LabelsQueryType, LabelsQueryVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		LabelsQuery,
		{
			variables: { start: 0 },
			notifyOnNetworkStatusChange: true,
		},
	);

	const {
		filters: { label },
		updateLabel,
	} = useContext(FiltersContext);

	const labelOptions = [
		{ label: intl.formatMessage(i18n.allCategories), value: '' },
		...(idx(data, (_) => _.teamLabels.nodes) || []).map((label) => ({
			label: label.name,
			value: label.name,
		})),
	];

	const selectedOption = {
		label: label || intl.formatMessage(i18n.allCategories),
		value: label,
	};

	const onLabelsMenuScrollToBottom = () => {
		const hasNextPage = idx(data, (_) => _.teamLabels.pageInfo.hasNextPage) || false;

		if (!loading && hasNextPage) {
			void fetchMore({
				variables: {
					start: idx(data, (_) => _.teamLabels.nodes.length) || 0,
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					const newLabels = idx(fetchMoreResult, (_) => _.teamLabels.nodes) || [];
					const newPageInfo =
						idx(fetchMoreResult, (_) => _.teamLabels.pageInfo) || prev.teamLabels!.pageInfo;
					const oldLabels = idx(prev, (_) => _.teamLabels.nodes) || [];

					return {
						...prev,
						teamLabels: {
							nodes: [...oldLabels, ...newLabels],
							pageInfo: newPageInfo,
							__typename: 'PaginatedLabelList',
						},
					};
				},
			});
		}
	};

	const onLabelsChange = (option: ValueType<Option>) => {
		updateLabel((option as Option).value);
	};

	return (
		<>
			<ScreenReaderOnlyText id={LABELS_FILTER_DESCRIPTION_ID} aria-hidden="true">
				{intl.formatMessage(i18n.categoryFilterLabel)}
			</ScreenReaderOnlyText>
			<Select
				styles={selectStyles}
				isLoading={loading && isOpen}
				options={labelOptions}
				onChange={onLabelsChange}
				value={selectedOption}
				onMenuScrollToBottom={onLabelsMenuScrollToBottom}
				onMenuOpen={() => setIsOpen(true)}
				onMenuClose={() => setIsOpen(false)}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className="space-labels-select"
				classNamePrefix="react-select"
				labelId={LABELS_FILTER_DESCRIPTION_ID}
			/>
		</>
	);
};
